import { useEffect, useState, useCallback } from "react";
import { Plus, CalendarRange, Pencil, Trash2, Loader2, Building2, Copy, Settings2, Star } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loading, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const MONTHS = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
const KIND_COLOR = { 研修: "#4ade80", 委員会: "#f97316", 訓練: "#111827" };
const PRESET = ["#f97316", "#4ade80", "#a855f7", "#fdba74", "#ec4899", "#111827", "#6366f1", "#fde68a", "#d1d5db", "#38bdf8"];
const KIND_BADGE = { 研修: "bg-blue-500/15 text-blue-300", 委員会: "bg-purple-500/15 text-purple-300", 訓練: "bg-teal-500/15 text-teal-300" };
const SERVICE_TYPES = ["就労継続支援B型", "就労継続支援A型", "就労移行支援", "生活介護", "放課後等デイサービス", "児童発達支援", "共同生活援助（GH）", "自立訓練"];
const EMPTY_ROW = { name: "", plan_kind: "研修", legal_min_count: 1, months: [], color: "#4ade80", category_id: null, master_id: null };

function toPayload(r, presetId) {
  return { name: r.name, plan_kind: r.plan_kind, legal_min_count: Number(r.legal_min_count) || 1, months: r.months || [], color: r.color || null, category_id: r.category_id || null, master_id: r.plan_kind === "研修" ? (r.master_id || null) : null, order: r.order || 0, preset_id: presetId };
}

function RowDialog({ initial, presetId, onClose, onSaved }) {
  const [f, setF] = useState(initial ? { ...EMPTY_ROW, ...initial } : EMPTY_ROW);
  const [busy, setBusy] = useState(false);
  const [masters, setMasters] = useState([]);
  useEffect(() => { api.get("/platform/master-trainings").then(({ data }) => setMasters((data || []).filter((m) => m.status === "公開"))).catch(() => {}); }, []);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) { toast.error("名称は必須です"); return; }
    setBusy(true);
    try {
      if (initial?.id) await api.put(`/platform/annual-plan-template/${initial.id}`, toPayload(f, presetId));
      else await api.post("/platform/annual-plan-template", toPayload(f, presetId));
      toast.success("保存しました");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <DialogContent data-testid="tpl-row-dialog">
      <DialogHeader><DialogTitle>{initial?.id ? "行を編集" : "行を追加"}</DialogTitle><DialogDescription>研修・委員会・訓練の名称、種別、法定回数、表示色を設定します。</DialogDescription></DialogHeader>
      <div className="space-y-3">
        <div><Label>名称 *</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1" data-testid="tpl-name" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>種別</Label>
            <Select value={f.plan_kind} onValueChange={(v) => { set("plan_kind", v); if (!initial?.id) set("color", KIND_COLOR[v]); }}>
              <SelectTrigger className="mt-1" data-testid="tpl-kind"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="研修">研修</SelectItem><SelectItem value="委員会">委員会</SelectItem><SelectItem value="訓練">訓練</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>法定最低回数</Label><Input type="number" min={1} value={f.legal_min_count} onChange={(e) => set("legal_min_count", e.target.value)} className="mt-1" data-testid="tpl-count" /></div>
        </div>
        {f.plan_kind === "研修" ? (
          <div>
            <Label>研修コンテンツ（動画教材の連携）</Label>
            <Select value={f.master_id || "none"} onValueChange={(v) => { const mid = v === "none" ? null : v; const m = masters.find((x) => x.id === v); set("master_id", mid); if (mid && !f.name.trim() && m) set("name", m.name); }}>
              <SelectTrigger className="mt-1" data-testid="tpl-master"><SelectValue placeholder="教材を選択" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">連携しない</SelectItem>
                {masters.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}（{m.category}）</SelectItem>)}
              </SelectContent>
            </Select>
            {f.master_id
              ? <p className="text-[11px] text-emerald-400 mt-1" data-testid="tpl-link-ok">教材連携済み：法人へ適用すると受講用の研修が自動作成され、在籍スタッフへ割り当てられます。</p>
              : <p className="text-[11px] text-amber-400 mt-1" data-testid="tpl-link-warn">未連携：このままだと法人へ適用しても受講用の研修は作成されません。上のプルダウンから教材を選んでください。</p>}
          </div>
        ) : (
          <div className="rounded-lg bg-slate-800/60 border border-slate-700 px-3 py-2" data-testid="tpl-kind-note">
            <p className="text-[11px] text-slate-400">「{f.plan_kind}」は動画教材との連携はありません（実施記録・議事録で管理します）。名称・実施月・法定回数を設定してください。</p>
          </div>
        )}
        <div>
          <Label>表示色</Label>
          <div className="flex items-center gap-2 mt-1">
            <input type="color" value={f.color || "#4ade80"} onChange={(e) => set("color", e.target.value)} className="h-9 w-12 rounded cursor-pointer bg-transparent" data-testid="tpl-color" />
            <div className="flex gap-1 flex-wrap">
              {PRESET.map((c) => <button key={c} onClick={() => set("color", c)} className="w-6 h-6 rounded border border-slate-600" style={{ background: c }} aria-label={c} />)}
            </div>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>キャンセル</Button>
        <Button onClick={save} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="tpl-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "保存"}</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function PresetDialog({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial ? { name: initial.name, service_type: initial.service_type || "", description: initial.description || "", is_default: !!initial.is_default } : { name: "", service_type: "", description: "", is_default: false });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) { toast.error("プリセット名は必須です"); return; }
    setBusy(true);
    const payload = { name: f.name, service_type: f.service_type || null, description: f.description || null, is_default: f.is_default };
    try {
      if (initial?.id) await api.put(`/platform/annual-plan-presets/${initial.id}`, payload);
      else await api.post("/platform/annual-plan-presets", payload);
      toast.success("保存しました");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <DialogContent data-testid="preset-dialog">
      <DialogHeader><DialogTitle>{initial?.id ? "プリセットを編集" : "プリセットを追加"}</DialogTitle><DialogDescription>サービス種別ごとの年間計画テンプレートを管理します。既定に設定すると自動適用に利用されます。</DialogDescription></DialogHeader>
      <div className="space-y-3">
        <div><Label>プリセット名 *</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1" data-testid="preset-name" /></div>
        <div><Label>サービス種別（新規契約時の自動適用に利用）</Label>
          <Select value={f.service_type || "none"} onValueChange={(v) => set("service_type", v === "none" ? "" : v)}>
            <SelectTrigger className="mt-1" data-testid="preset-service"><SelectValue placeholder="指定なし" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">指定なし（共通）</SelectItem>
              {SERVICE_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label>説明</Label><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} className="mt-1" rows={2} data-testid="preset-desc" /></div>
        <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={f.is_default} onChange={(e) => set("is_default", e.target.checked)} data-testid="preset-default" />既定プリセットにする（サービス種別が一致しない法人へ自動適用）</label>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>キャンセル</Button>
        <Button onClick={save} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="preset-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "保存"}</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function ApplyDialog({ presets, currentPreset, onClose }) {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [presetId, setPresetId] = useState(currentPreset || "");
  const [year, setYear] = useState(new Date().getFullYear());
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/platform/companies").then(({ data }) => setCompanies(data)); }, []);
  const apply = async () => {
    if (!companyId) { toast.error("法人を選択してください"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/platform/annual-plan-template/apply", { company_id: companyId, fiscal_year: Number(year), preset_id: presetId || null });
      toast.success(`適用しました（${data.inserted}件 / ${data.offices}事業所 / ${data.preset || "既定"}）`);
      onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <DialogContent data-testid="tpl-apply-dialog">
      <DialogHeader><DialogTitle>法人へ年間計画を適用</DialogTitle><DialogDescription>選択した法人・年度の年間計画を、選んだプリセットで上書きします。</DialogDescription></DialogHeader>
      <div className="space-y-3">
        <p className="text-sm text-slate-500">選択した法人の対象年度の年間計画を、選んだプリセットで<strong>上書き</strong>します（全事業所）。</p>
        <div><Label>法人</Label>
          <Select value={companyId} onValueChange={setCompanyId}>
            <SelectTrigger className="mt-1" data-testid="apply-company"><SelectValue placeholder="法人を選択" /></SelectTrigger>
            <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>プリセット</Label>
          <Select value={presetId} onValueChange={setPresetId}>
            <SelectTrigger className="mt-1" data-testid="apply-preset"><SelectValue /></SelectTrigger>
            <SelectContent>{presets.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.is_default ? "（既定）" : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>年度</Label><Input type="number" value={year} onChange={(e) => setYear(e.target.value)} className="mt-1 w-40" data-testid="apply-year" /></div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>キャンセル</Button>
        <Button onClick={apply} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="apply-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "適用する"}</Button>
      </DialogFooter>
    </DialogContent>
  );
}

export default function PlatformAnnualPlan() {
  const [presets, setPresets] = useState(null);
  const [presetId, setPresetId] = useState("");
  const [rows, setRows] = useState(null);
  const [rowDialog, setRowDialog] = useState(undefined);
  const [presetDialog, setPresetDialog] = useState(undefined);
  const [applyOpen, setApplyOpen] = useState(false);

  const loadPresets = useCallback(async () => {
    const { data } = await api.get("/platform/annual-plan-presets");
    setPresets(data);
    setPresetId((cur) => (cur && data.some((p) => p.id === cur)) ? cur : ((data.find((p) => p.is_default) || data[0])?.id || ""));
  }, []);
  useEffect(() => { loadPresets(); }, [loadPresets]);

  const loadRows = useCallback(() => {
    if (!presetId) { setRows([]); return; }
    api.get(`/platform/annual-plan-template?preset_id=${presetId}`).then(({ data }) => setRows(data));
  }, [presetId]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const currentPreset = (presets || []).find((p) => p.id === presetId);

  const toggle = async (row, m) => {
    const has = (row.months || []).includes(m);
    const months = has ? row.months.filter((x) => x !== m) : [...(row.months || []), m].sort((a, b) => a - b);
    setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, months } : r)));
    try { await api.put(`/platform/annual-plan-template/${row.id}`, toPayload({ ...row, months }, presetId)); }
    catch (e) { toast.error(apiErr(e)); loadRows(); }
  };
  const removeRow = async (row) => {
    if (!window.confirm(`「${row.name}」を削除しますか？`)) return;
    try { await api.delete(`/platform/annual-plan-template/${row.id}`); toast.success("削除しました"); loadRows(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const duplicatePreset = async () => {
    if (!currentPreset) return;
    const name = window.prompt("複製後のプリセット名", `${currentPreset.name}（コピー）`);
    if (!name) return;
    try {
      const { data } = await api.post(`/platform/annual-plan-presets/${presetId}/duplicate`, { name });
      toast.success(`複製しました（${data.rows}行）`);
      await loadPresets();
      setPresetId(data.id);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const deletePreset = async () => {
    if (!currentPreset) return;
    if (!window.confirm(`プリセット「${currentPreset.name}」と、その全行を削除しますか？`)) return;
    try { await api.delete(`/platform/annual-plan-presets/${presetId}`); toast.success("削除しました"); setPresetId(""); await loadPresets(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!presets || rows === null) return <Loading />;

  return (
    <div data-testid="platform-annual-plan">
      <div className="flex items-end justify-between gap-3 mb-5 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">年間計画設定（標準テンプレート）</h1>
          <p className="text-sm text-slate-400 mt-1">サービス種別ごとにプリセットを用意し、各法人へ一括適用・契約時に自動適用できます</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800" onClick={() => setApplyOpen(true)} data-testid="apply-template-btn"><Building2 className="w-4 h-4 mr-1" />法人へ適用</Button>
          <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900" onClick={() => setRowDialog(null)} disabled={!presetId} data-testid="add-template-row-btn"><Plus className="w-4 h-4 mr-1" />行を追加</Button>
        </div>
      </div>

      {/* Preset toolbar */}
      <div className="flex items-center gap-2 mb-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-3">
        <span className="text-xs text-slate-400 mr-1">プリセット</span>
        <Select value={presetId} onValueChange={setPresetId}>
          <SelectTrigger className="w-64 bg-slate-800 border-slate-700 text-slate-100" data-testid="preset-select"><SelectValue placeholder="プリセットを選択" /></SelectTrigger>
          <SelectContent>{presets.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.is_default ? "（既定）" : ""}{p.service_type ? ` · ${p.service_type}` : ""}</SelectItem>)}</SelectContent>
        </Select>
        {currentPreset?.is_default && <span className="flex items-center gap-1 text-[11px] text-amber-400"><Star className="w-3.5 h-3.5 fill-amber-400" />既定</span>}
        <div className="flex-1" />
        <Button size="sm" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800" onClick={() => setPresetDialog(null)} data-testid="add-preset-btn"><Plus className="w-4 h-4 mr-1" />プリセット追加</Button>
        <Button size="sm" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800" onClick={duplicatePreset} disabled={!presetId} data-testid="dup-preset-btn"><Copy className="w-4 h-4 mr-1" />複製</Button>
        <Button size="sm" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800" onClick={() => setPresetDialog(currentPreset)} disabled={!presetId} data-testid="edit-preset-btn"><Settings2 className="w-4 h-4 mr-1" />編集</Button>
        <Button size="sm" variant="outline" className="border-slate-700 text-rose-400 hover:bg-slate-800" onClick={deletePreset} disabled={!presetId} data-testid="del-preset-btn"><Trash2 className="w-4 h-4" /></Button>
      </div>

      {rows.length ? (
        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-x-auto">
          <table className="w-full border-collapse text-sm" style={{ minWidth: 920 }}>
            <thead>
              <tr className="border-b border-slate-800">
                <th className="sticky left-0 z-10 bg-slate-900 text-left font-semibold text-slate-300 px-4 py-3 w-56">研修・訓練内容</th>
                {MONTHS.map((m) => <th key={m} className="text-center font-medium text-slate-400 px-1 py-3 w-14">{m}月</th>)}
                <th className="px-2 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-slate-800/60 hover:bg-slate-800/30 group" data-testid={`template-row-${row.id}`}>
                  <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-900 px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: row.color || KIND_COLOR[row.plan_kind] }} />
                      <span className="text-slate-100 leading-tight">{row.name}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${KIND_BADGE[row.plan_kind] || "bg-slate-700 text-slate-300"}`}>{row.plan_kind}</span>
                      <span className="text-[10px] text-slate-500">法定{row.legal_min_count}回</span>
                      {row.plan_kind === "研修" ? (
                        row.master_id
                          ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300" data-testid={`row-link-ok-${row.id}`}>教材連携済み</span>
                          : <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300" data-testid={`row-link-warn-${row.id}`}>教材未連携</span>
                      ) : (
                        <span className="text-[10px] text-slate-500">記録管理</span>
                      )}
                    </div>
                  </td>
                  {MONTHS.map((m) => {
                    const on = (row.months || []).includes(m);
                    return (
                      <td key={m} className="text-center px-1 py-2.5">
                        <button onClick={() => toggle(row, m)} data-testid={`template-cell-${row.id}-${m}`} aria-pressed={on}
                          className={`w-8 h-6 rounded-sm mx-auto block transition-all ${on ? "shadow ring-1 ring-white/50" : "bg-slate-800 hover:bg-slate-700 border border-slate-700"}`}
                          style={on ? { background: row.color || KIND_COLOR[row.plan_kind] } : undefined} title={`${row.name} / ${m}月`} />
                      </td>
                    );
                  })}
                  <td className="px-2">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => setRowDialog(row)} className="text-slate-400 hover:text-amber-400" data-testid={`edit-template-${row.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => removeRow(row)} className="text-slate-400 hover:text-rose-400" data-testid={`del-template-${row.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState title="このプリセットには行がありません" hint="「行を追加」から研修・委員会・訓練を追加してください" />
      )}

      <div className="mt-4 flex items-center gap-4 text-xs text-slate-400 flex-wrap">
        <span className="flex items-center gap-1.5"><CalendarRange className="w-4 h-4" />月のセルをクリックして実施予定を切り替えます</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: KIND_COLOR["委員会"] }} />委員会</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: KIND_COLOR["研修"] }} />研修</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: KIND_COLOR["訓練"] }} />訓練</span>
        <span className="flex items-center gap-1.5"><span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300">教材連携済み</span>研修が動画教材とひも付き済み</span>
        <span className="flex items-center gap-1.5"><span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">教材未連携</span>要設定（適用しても研修が作られません）</span>
      </div>

      <Dialog open={rowDialog !== undefined} onOpenChange={(o) => !o && setRowDialog(undefined)}>
        {rowDialog !== undefined && <RowDialog initial={rowDialog} presetId={presetId} onClose={() => setRowDialog(undefined)} onSaved={() => { setRowDialog(undefined); loadRows(); }} />}
      </Dialog>
      <Dialog open={presetDialog !== undefined} onOpenChange={(o) => !o && setPresetDialog(undefined)}>
        {presetDialog !== undefined && <PresetDialog initial={presetDialog} onClose={() => setPresetDialog(undefined)} onSaved={() => { setPresetDialog(undefined); loadPresets(); }} />}
      </Dialog>
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        {applyOpen && <ApplyDialog presets={presets} currentPreset={presetId} onClose={() => setApplyOpen(false)} />}
      </Dialog>
    </div>
  );
}
